'use client';

import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { ARABIC_ALPHABET } from '@/lib/alphabet';
import { getArabic } from '@/lib/quran-data';
import { Eraser, RefreshCw, Download, Feather } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function WriteSection() {
  const { uiLang, surah, translationEd } = useApp();
  const t = DICTS[uiLang];
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [letterIdx, setLetterIdx] = useState(1);
  const [clearKey, setClearKey] = useState(0);
  const [copyIdx, setCopyIdx] = useState(0);
  const [hasDrawn, setHasDrawn] = useState(false);
  const letter = ARABIC_ALPHABET[letterIdx];
  const arabic = getArabic(surah);

  // Draw the guide letter (dotted) on the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const size = canvas.offsetWidth;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);

    // Grid guides
    ctx.strokeStyle = 'rgba(212,175,55,0.10)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath(); ctx.moveTo((size / 4) * i, 0); ctx.lineTo((size / 4) * i, size); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, (size / 4) * i); ctx.lineTo(size, (size / 4) * i); ctx.stroke();
    }

    // Dotted guide letter
    ctx.save();
    ctx.font = `${size * 0.62}px var(--font-amiri), Amiri, serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.setLineDash([5, 7]);
    ctx.strokeStyle = 'rgba(212,175,55,0.55)';
    ctx.lineWidth = 1.6;
    ctx.strokeText(letter.char, size / 2, size / 2 + size * 0.02);
    ctx.restore();
  }, [letterIdx, letter.char, clearKey]);

  const changeLetter = (idx: number) => {
    setLetterIdx(((idx % ARABIC_ALPHABET.length) + ARABIC_ALPHABET.length) % ARABIC_ALPHABET.length);
    setHasDrawn(false);
  };

  const pointer = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pointer(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#f0d98c';
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointer(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => { setClearKey((k) => k + 1); setHasDrawn(false); };

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.download = `trace_lettre_${letter.name}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  };

  const verse = arabic[copyIdx % arabic.length];

  return (
    <>
      <PageBanner
        src="/images/banners/write_banner.png"
        eyebrow="Calligraphie — الخط"
        title={t.write.title}
        sub={t.write.subtitle}
      />
      <section id="section-write" className="mx-auto max-w-5xl scroll-mt-20 px-4 py-14">

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tracing board */}
        <div className="gold-ornate-card rounded-2xl p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-serif-royal text-lg font-bold text-[#f0d98c]">{t.write.canvas_title}</h3>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => changeLetter(letterIdx - 1)}
                className="rounded-md border border-[rgba(212,175,55,0.35)] px-2.5 py-1.5 text-xs font-bold text-[#e8dcc0] hover:bg-[rgba(212,175,55,0.1)]"
                aria-label="previous letter"
              >
                ←
              </button>
              <select
                value={letterIdx}
                onChange={(e) => changeLetter(Number(e.target.value))}
                className="rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-2 py-1.5 text-xs text-[#f5f0e1] outline-none"
              >
                {ARABIC_ALPHABET.map((l, i) => (
                  <option key={l.name + i} value={i}>{l.char} — {l.name}</option>
                ))}
              </select>
              <button
                onClick={() => changeLetter(letterIdx + 1)}
                className="rounded-md border border-[rgba(212,175,55,0.35)] px-2.5 py-1.5 text-xs font-bold text-[#e8dcc0] hover:bg-[rgba(212,175,55,0.1)]"
                aria-label="next letter"
              >
                →
              </button>
            </div>
          </div>
          <p className="mb-3 text-xs text-[#cfe0cf]/65">{t.write.canvas_desc}</p>

          <div className="relative mx-auto aspect-square w-full max-w-md overflow-hidden rounded-xl border-2 border-[rgba(212,175,55,0.35)] bg-[#04241c]">
            <canvas
              ref={canvasRef}
              className="h-full w-full cursor-crosshair touch-none"
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={end}
              onPointerLeave={end}
            />
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => clear()}
              className="flex items-center gap-1.5 rounded-md border border-[rgba(212,175,55,0.4)] px-4 py-2 text-xs font-semibold text-[#f0d98c] hover:bg-[rgba(212,175,55,0.12)]"
            >
              <Eraser className="h-3.5 w-3.5" /> {t.write.clear}
            </button>
            <button
              onClick={() => changeLetter(Math.floor(Math.random() * ARABIC_ALPHABET.length))}
              className="flex items-center gap-1.5 rounded-md border border-[rgba(212,175,55,0.4)] px-4 py-2 text-xs font-semibold text-[#f0d98c] hover:bg-[rgba(212,175,55,0.12)]"
            >
              <RefreshCw className="h-3.5 w-3.5" /> {t.write.new_letter}
            </button>
            <button
              onClick={download}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-semibold transition-colors',
                hasDrawn
                  ? 'bg-[#d4af37] text-[#032019]'
                  : 'cursor-not-allowed border border-[rgba(212,175,55,0.25)] text-[#cfe0cf]/40'
              )}
              disabled={!hasDrawn}
            >
              <Download className="h-3.5 w-3.5" /> {t.write.save_drawing}
            </button>
          </div>
        </div>

        {/* Copy exercise */}
        <div className="gold-ornate-card rounded-2xl p-6">
          <h3 className="mb-4 font-serif-royal text-lg font-bold text-[#f0d98c]">{t.write.copy_title}</h3>
          <p className="mb-4 text-xs text-[#cfe0cf]/65">{t.write.copy_desc}</p>

          <div className="rounded-xl border border-[rgba(212,175,55,0.3)] bg-[#04241c] p-6 text-center">
            <p className="font-arabic text-3xl leading-[2] text-[#f5f0e1]" dir="rtl" lang="ar">{verse?.text}</p>
            <p className="mt-2 text-xs font-semibold tracking-widest text-[#d4af37]/70">SOURATE {surah} — VERSET {verse?.n}</p>
          </div>

          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setCopyIdx((copyIdx + 1) % arabic.length)}
              className="flex items-center gap-2 rounded-md bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-5 py-2.5 text-xs font-bold text-[#032019] transition-transform hover:scale-[1.03]"
            >
              <Feather className="h-3.5 w-3.5" /> {t.write.next_verse}
            </button>
          </div>

          {/* Calligraphy style hint */}
          <div className="mt-6 rounded-lg border border-dashed border-[rgba(212,175,55,0.35)] p-4 text-center">
            <p className="text-[11px] leading-relaxed text-[#cfe0cf]/60">
              {uiLang === 'ar'
                ? 'اكتب بقلم عريض، ابدأ من اليمين، وحافظ على تناسق الحروف'
                : uiLang === 'en'
                  ? 'Use a broad pen, start from the right, keep the letters flowing evenly'
                  : 'Utilisez un calame large, commencez par la droite, gardez les lettres fluides et régulières'}
            </p>
          </div>
        </div>
      </div>
      </section>
    </>
  );
}
